import { Sprout, BowArrow, Binoculars, Eye } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface SkillTier {
  name: string
  /** Correct-outcome % at/above which a player enters this tier. */
  minPct: number
  icon: LucideIcon
  /** CSS color value (var with fallback), reusing the GameCard point-tier
   * colors 1:1 so a color means the same thing across the app.
   * This is the card's own FILL ("-tint") — used for the icon and the skill-
   * meter bar fill, which want the lighter, more saturated shade, not the
   * darker ink meant for text-on-fill. */
  color: string
  /** The card's ink ("-strong") — the darker shade GameCard uses for team-
   * name/score text on top of `color`'s fill. Used for the active tier
   * label's text and its 1px border, so that pill gets the same
   * fill-with-ink-outline treatment the actual point-tier cards use instead
   * of low-contrast tint-on-tint text. */
  inkColor: string
  /** The tier hue as text/iconography on a NEUTRAL surface ("-text", ticket
 *) — for the tier icons, which are drawn bare on the card and on the
   * info modal's sunken rows with no tint fill behind them. `inkColor` is
   * graded near-black in light mode precisely because it sits on a fill, so
   * the icons read as black there; `color`'s tint is too washed out in dark
   * mode (~16-18% alpha), which is why they were on the ink to begin with.
   * In dark mode the "-text" tokens alias "-strong", so this is the same
   * value `inkColor` resolves to and nothing changes there. */
  textColor: string
}

export const SKILL_TIERS: SkillTier[] = [
  {
    name: 'Rookie',
    minPct: 0,
    icon: Sprout,
    color: 'var(--color-negative-tint)',
    inkColor: 'var(--color-negative-strong)',
    textColor: 'var(--color-accent-tier-miss-text, var(--color-negative-strong))',
  },
  {
    name: 'Sharp',
    minPct: 45,
    icon: BowArrow,
    color:
      'var(--color-accent-tier-partial-tint, var(--color-text-secondary))',
    inkColor:
      'var(--color-accent-tier-partial-strong, var(--color-text-secondary))',
    textColor:
      'var(--color-accent-tier-partial-text, var(--color-text-secondary))',
  },
  {
    name: 'Analyst',
    minPct: 52,
    icon: Binoculars,
    color:
      'var(--color-accent-tier-outcome-tint, var(--color-accent-info-strong))',
    inkColor:
      'var(--color-accent-tier-outcome-strong, var(--color-accent-info-strong))',
    textColor:
      'var(--color-accent-tier-outcome-text, var(--color-accent-info-strong))',
  },
  {
    name: 'Oracle',
    minPct: 58,
    icon: Eye,
    // Exact/oracle violet — kept off --color-accent-win-*
    // directly since that token is also consumed elsewhere for an unrelated
    // green "win" identity that resolves differently in light mode.
    color: 'var(--color-accent-tier-exact-tint, var(--color-accent-win-strong))',
    inkColor:
      'var(--color-accent-tier-exact-strong, var(--color-accent-win-strong))',
    textColor:
      'var(--color-accent-tier-exact-text, var(--color-accent-win-strong))',
  },
]

export function getSkillTierIndex(pct: number): number {
  let index = 0
  for (let i = 0; i < SKILL_TIERS.length; i++) {
    if (pct >= SKILL_TIERS[i].minPct) index = i
  }
  return index
}
